import { type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { KeyboardKey } from "@/components/keyboard/KeyboardKey";
import { cn } from "@/components/podkit/lib/cn";
import { AgentResponseBlock_TodoItem_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";
import { Agents, type PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionLoading, getActiveEnvironmentId } from "@/utils/agent-executions";
import type { TodoItem } from "./TodoList";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AgentExecutionStatusLabel } from "@/components/ai/AgentExecutionStatusLabel";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { TextShimmer } from "@/components/flexkit/TextShimmer";
import { Counter } from "@/components/flexkit/badge/Counter";

type TodoListHeaderProps = {
    todos: TodoItem[];
    agentExecution: PlainAgentExecution;
    isOpen: boolean;
};

export const TodoListHeader: FC<TodoListHeaderProps> = ({ todos, agentExecution, isOpen }) => {
    const hasTodos = todos.length > 0;
    const environmentId = getActiveEnvironmentId(agentExecution);
    const { data: environment } = useEnvironment(environmentId);

    return (
        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
                {hasTodos &&
                    (isOpen ? (
                        <ChevronDown size={14} className="text-content-tertiary" aria-hidden="true" />
                    ) : (
                        <ChevronRight size={14} className="text-content-tertiary" aria-hidden="true" />
                    ))}

                <div className="flex min-w-0 items-center gap-2">
                    <TodoListHeaderContent agentExecution={agentExecution} environment={environment} todos={todos} />
                </div>
            </div>

            {hasTodos && (
                <div className="flex items-center gap-2">
                    <TodoCounter todos={todos} />
                </div>
            )}
        </div>
    );
};

export const TodoListHeaderContent: FC<{
    agentExecution?: PlainAgentExecution;
    environment?: PlainEnvironment;
    todos: TodoItem[];
}> = ({ agentExecution, environment, todos }) => {
    const hasTodos = todos.length > 0;

    // If there's no agent execution we don't have a status to show.
    if (!agentExecution || !environment) {
        return null;
    }

    // If the agent execution is starting, then we show that instead of TODOSs
    const isInEnvironmentAgent = agentExecution.spec?.agentId === Agents.InEnvironmentOna.id;
    const isEnvironmentStarting =
        environment?.status?.phase === EnvironmentPhase.STARTING ||
        environment?.status?.phase === EnvironmentPhase.CREATING;
    if (isInEnvironmentAgent && isEnvironmentStarting) {
        return (
            <Text className="font-mono text-base text-content-primary">
                Waiting for environment to start
                <span className="animate-dots">.</span>
            </Text>
        );
    }

    // If it's running and we have TODOs, then we show those
    if (hasTodos) {
        return <TodoStatusText todos={todos} />;
    }

    // Otherwise we fallback to just the agent execution status.
    return (
        <AgentExecutionStatusLabel
            agentExecution={agentExecution}
            className="font-mono text-base text-content-primary"
        />
    );
};

export const TodoCounter: FC<{ todos: TodoItem[] }> = ({ todos }) => {
    const { completedCount, totalCount, allCompleted } = getTodoStats(todos);
    const label = `${completedCount}/${totalCount}`;
    return <Counter variant={allCompleted ? "success" : "default"} counter={label} />;
};

export const TodoStatusText: FC<{ todos: TodoItem[] }> = ({ todos }) => {
    if (todos.length === 0) return null;

    const { allCompleted } = getTodoStats(todos);
    const hasInProgressItems = todos.some((todo) => todo.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS);
    const allCleared = todos.every((todo) => todo.status === AgentResponseBlock_TodoItem_Phase.CLEARED);

    if (allCleared) {
        return <Text className="truncate font-mono text-base text-content-primary">Todos cleared</Text>;
    }

    if (allCompleted) {
        return <Text className="truncate font-mono text-base text-content-primary">Todos complete</Text>;
    }

    if (hasInProgressItems) {
        const currentTodoTitle = getInProgressTodoTitle(todos);
        return (
            <>
                <TextShimmer className="hidden truncate font-mono text-base lg:block">{currentTodoTitle}</TextShimmer>
                <Text className="w-full truncate text-left font-mono text-base text-content-primary lg:hidden">
                    {currentTodoTitle}
                    <span className="animate-dots lg:hidden">.</span>
                </Text>
            </>
        );
    }

    return <Text className="truncate font-mono text-base font-normal text-content-primary">Todos pending</Text>;
};

export const StopButton: FC<{ agentExecution: PlainAgentExecution }> = ({ agentExecution }) => {
    const { handleStopAgentExecution } = useAgentExecutionActions(agentExecution);

    const isButtonDisabled = !agentExecution || !agentExecutionLoading(agentExecution);

    const handleClick = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        await handleStopAgentExecution();
    };

    return (
        <div
            className={cn(
                "flex h-6 cursor-pointer items-center gap-2 rounded text-sm",
                isButtonDisabled ? "cursor-not-allowed opacity-50" : "text-content-secondary",
            )}
            onClick={isButtonDisabled ? undefined : handleClick}
            data-tracking-id="stop-agent-execution-todo-list"
        >
            <span
                className={
                    agentExecution && agentExecutionLoading(agentExecution)
                        ? "text-content-primary"
                        : "text-content-secondary"
                }
            >
                Stop
            </span>
            <KeyboardKey
                className={cn(
                    "bg-content-secondary/20 dark:bg-content-secondary/40",
                    agentExecution && agentExecutionLoading(agentExecution)
                        ? "text-content-primary"
                        : "text-content-secondary",
                )}
            >
                Esc
            </KeyboardKey>
        </div>
    );
};

const getTodoStats = (todos: TodoItem[]) => {
    const activeTodos = todos.filter((todo) => todo.status !== AgentResponseBlock_TodoItem_Phase.CLEARED);
    const completedCount = activeTodos.filter((todo) => todo.status === AgentResponseBlock_TodoItem_Phase.DONE).length;
    const inProgressIndex = activeTodos.findIndex(
        (todo) => todo.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS,
    );
    const totalCount = activeTodos.length;
    const allCompleted = completedCount === totalCount && totalCount > 0;

    // If there's an in-progress todo, show its position (1-based index)
    // Otherwise, show completed count
    const currentPosition = inProgressIndex !== -1 ? inProgressIndex + 1 : completedCount;

    return { completedCount: currentPosition, totalCount, allCompleted };
};

const getInProgressTodoTitle = (todos: TodoItem[]): string => {
    const inProgressTodo = todos.find((todo) => todo.status === AgentResponseBlock_TodoItem_Phase.IN_PROGRESS);
    return inProgressTodo ? inProgressTodo.title : "Processing todos";
};
